import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import {
  isUserActionNeedPhotoVerification,
  isUserNeedVerification,
  NotificationManager,
  PhotoVerificationAction,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useCasinoFavorites,
  useCasinoNewState,
} from '@atlas/core';
import { casinoRoutes, staticRoutes } from '@atlas/routing';
import { CASINO_TILE_TYPES, CasinoGameTile } from '@atlas/ui';

import { CXFROM_STORAGE_KEY, SIGN_IN_REDIRECT_URL_QUERY_KEY, ACCOUNT_STATUSES } from 'common/constants';
import { useSignupPopup } from 'components/SignUp/useSignUpPopup';
import { CDN_TYPE_GAME, imageUrlCDN } from 'common/utils';
import { useAccountBlocked } from 'hooks/useAccountBlocked';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';

import { callUserSelfExcludedPopup, normalizePath } from '../utils';

const { game: casinoGamePath } = casinoRoutes;

const InfoGame = ({ game, gameItemClass, isFavorites = false, size = 'M' }) => {
  const casinoGameTileType = SiteConfigManager.getConfig(
    'config.config.general.desktopCasinoGameTileView',
    CASINO_TILE_TYPES.TYPE_5,
  );
  const demoButtonStateForSignedInUsers = SiteConfigManager.getConfig(
    'config.config.casino.desktop_demo_logged',
    false,
  );
  const demoButtonStateForNonSignedInUsers = SiteConfigManager.getConfig(
    'config.config.casino.desktop_demo_non_logged',
    false,
  );
  const {
    demoAvailable,
    favoritesCount,
    iconUrl,
    isDemoAvailable,
    name,
    providerDisplayedName,
    providerName,
  } = game;
  const casinoNewState = useCasinoNewState();
  const isAccountBlocked = useAccountBlocked();

  const {
    addToFavorites,
    favoriteGames: { counters, data: favoriteGames },
    removeFromFavorites,
  } = casinoNewState;
  const { removeFromFavorites: removeFavoriteGameFromFavorites } = useCasinoFavorites();

  const history = useHistory();
  const {
    location: { pathname },
    push: historyPush,
  } = history;
  const image = iconUrl ? imageUrlCDN(iconUrl, CDN_TYPE_GAME) : null;

  const applicationState = useApplicationState();
  const { account = {}, account: { selfExcludedDueDate } = {}, authenticated } = applicationState;
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();
  const { openSignupPopup } = useSignupPopup();
  const showInPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);

  const path = normalizePath(pathname);

  const onSelectGame = useCallback(async () => {
    if (isAccountBlocked) {
      NotificationManager.display({
        id: 'accountBlocked',
        type: 'accountBlocked',
      });
      return;
    }

    // if user is self excluded
    if (selfExcludedDueDate && dayjs().isBefore(selfExcludedDueDate)) {
      return callUserSelfExcludedPopup();
    }

    const isPartiallyRegistered = account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED;

    if (isPartiallyRegistered) {
      if (showInPopup) {
        openSignupPopup();
      } else {
        historyPush(staticRoutes.signup);
      }
      return;
    }

    if (
      isUserNeedVerification(applicationState.account) &&
      isUserActionNeedPhotoVerification(
        applicationState.account,
        PhotoVerificationAction.CASINO_GAME,
      )
    ) {
      return NotificationManager.display({
        data: { history },
        message: 'verificationPopup',
        type: 'verificationPopup',
      });
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    const gameUrl = new URL(`${path}/${casinoGamePath}/${game.id}`, location.origin);

    gameUrl.searchParams.set('provider', providerName);
    gameUrl.searchParams.set('from', location.pathname + location.search);

    if (!authenticated) {
      const url = new URL(path, window.location.origin);
      url.searchParams.set('cmd', 'signin');
      url.searchParams.set(SIGN_IN_REDIRECT_URL_QUERY_KEY, gameUrl.pathname + gameUrl.search);
      return historyPush(url.search);
    }

    return historyPush(gameUrl.pathname + gameUrl.search);
  }, [
    isAccountBlocked,
    selfExcludedDueDate,
    applicationState.account,
    showSpentLimitPopupIfNeeded,
    showSelfRegulationPopupIfNeeded,
    path,
    game.id,
    providerName,
    account.status,
    authenticated,
    historyPush,
    history,
    openSignupPopup,
    showInPopup,
  ]);

  const onSelectDemoGame = useCallback(async () => {
    if (isAccountBlocked) {
      NotificationManager.display({
        id: 'accountBlocked',
        type: 'accountBlocked',
      });
      return;
    }

    const demoGameUrl = new URL(`${path}/${casinoGamePath}/${game.id}`, location.origin);
    demoGameUrl.searchParams.set('demo', 'true');
    demoGameUrl.searchParams.set('provider', providerName);
    demoGameUrl.searchParams.set('from', location.pathname + location.search);

    if (
      isUserNeedVerification(applicationState.account) &&
      isUserActionNeedPhotoVerification(
        applicationState.account,
        PhotoVerificationAction.CASINO_GAME,
      )
    ) {
      return NotificationManager.display({
        data: { history },
        message: 'verificationPopup',
        type: 'verificationPopup',
      });
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    historyPush(demoGameUrl.pathname + demoGameUrl.search);
  }, [
    isAccountBlocked,
    path,
    game.id,
    providerName,
    applicationState.account,
    showSpentLimitPopupIfNeeded,
    showSelfRegulationPopupIfNeeded,
    historyPush,
    history,
  ]);

  const isFavoriteGame = useMemo(() => favoriteGames.includes(game.id), [favoriteGames, game.id]);

  const toggleFavorite = useCallback(() => {
    if (!authenticated) {
      StorageManager.setItem(CXFROM_STORAGE_KEY, path);
      return historyPush(`${path}?cmd=signin`);
    }

    const { id } = game;

    if (isFavorites) {
      return removeFavoriteGameFromFavorites(id);
    }

    if (isFavoriteGame) {
      removeFromFavorites(id);
    } else {
      addToFavorites(id);
    }
  }, [
    addToFavorites,
    authenticated,
    game,
    historyPush,
    isFavoriteGame,
    isFavorites,
    path,
    removeFavoriteGameFromFavorites,
    removeFromFavorites,
  ]);

  const demo = useMemo(() => {
    if (
      (authenticated && !demoButtonStateForSignedInUsers) ||
      (!authenticated && !demoButtonStateForNonSignedInUsers)
    ) {
      return undefined;
    }

    if (isDemoAvailable || demoAvailable) {
      return {
        onClick: onSelectDemoGame,
      };
    }

    return undefined;
  }, [
    isDemoAvailable,
    demoAvailable,
    authenticated,
    demoButtonStateForSignedInUsers,
    demoButtonStateForNonSignedInUsers,
    onSelectDemoGame,
  ]);

  const favorite = useMemo(
    () => ({
      count: counters[game.id] || favoritesCount,
      onClick: toggleFavorite,
      state: isFavorites || isFavoriteGame,
    }),
    [counters, favoritesCount, game.id, isFavoriteGame, isFavorites, toggleFavorite],
  );

  if (!casinoNewState) {
    return null;
  }

  return (
    <CasinoGameTile
      className={gameItemClass}
      demo={demo}
      favorite={favorite}
      image={image}
      name={name}
      onClickPlay={onSelectGame}
      provider={providerDisplayedName}
      size={size}
      type={casinoGameTileType}
    />
  );
};

InfoGame.propTypes = {
  game: PropTypes.shape({
    iconUrl: PropTypes.string,
    id: PropTypes.number,
  }).isRequired,
  gameItemClass: PropTypes.string.isRequired,
  isFavorites: PropTypes.bool,
  size: PropTypes.string,
};

export default InfoGame;
